import {useRouter} from 'next/router';
import {BigNumber, FormattableCurrency, parseUnits} from '@ttx/core';
import {useCallback, useEffect, useState} from 'react';
import {VoidSigner} from '@ethersproject/abstract-signer';
import {PaymentDetailsFormProps} from '../components/purchase-or-download-cta/purchase-cta/cash-payment-details-modal/cash-payment-details-modal';
import {WalletNetwork} from '../contexts/authentication-context/types';
import {useSmartContractContext} from '../contexts/smart-contract/context';
import {CirclePay} from '../utils/payment/circle-pay-api';
import {useCheckoutModals} from './use-checkout-modals';
import {CashValues, CryptoValues, ModalVariant} from '../utils/types';

import {buyWithCash} from './utils/buy-with-cash';
import {useCheckoutHelpers} from './use-checkout-helpers';
import {useToastContext} from '../contexts/toast-context';
import axios from 'axios';
import {usePriceConversion} from './use-price-conversion';

const checkout = new CirclePay();
const USER_MAX_MINT_LIMIT = 200;

export const useMint: (props: {
  network?: WalletNetwork;
  ipAddress?: string;
  url: string;
  trackName?: string;
  artist?: string;
  imageSrc?: string;
  id?: string;
  currencySymbol: FormattableCurrency;
  buyoutPrice: BigNumber;
  quantity: BigNumber;
  isAllowedUser?: boolean;
  whiteList?: string[];
  country: string;
}) => {
  handleMintWithCrypto: () => Promise<any>;
  handleMintWithCash: (values: PaymentDetailsFormProps) => Promise<void>;
  monetaryValues: {
    crypto: CryptoValues;
    cash: CashValues;
  };
  paymentStatus?: string;
  modalVariant: ModalVariant | null;
  modalVariantComponent: JSX.Element | null;
  setModalVariant: (modalType: ModalVariant | null) => void;
  handleStartCheckoutFlow: () => void;
  loadingTreasuryBalance?: boolean;
} = ({
  network,
  ipAddress,
  url,
  trackName,
  artist,
  imageSrc,
  id,
  currencySymbol,
  buyoutPrice,
  quantity,
  isAllowedUser,
  whiteList = [],
  country,
}) => {
  const router = useRouter();
  const [primaryMintCount, setPrimaryMintCount] = useState<number>(0);
  const {traxxStemzContract} = useSmartContractContext();

  const {
    monetaryValues,
    paymentStatus,
    modalVariant,
    setModalVariant,
    loadingTreasuryBalance,
    onIncrement,
    onDecrement,
    onResetQuantity,
    walletNetwork,
    userId,
    userEmail,
    canUseCash,
    purchaseQuantity,
    walletAddress,
  } = useCheckoutHelpers({
    network,
    currencySymbol,
    buyoutPrice,
    quantity,
    maxAllowedUnits: USER_MAX_MINT_LIMIT,
  });

  const toast = useToastContext();

  const getUserMintInfo = useCallback(async () => {
    if (!walletAddress || !walletNetwork || modalVariant !== null) return;
    const loadedTraxxStemzContract = await traxxStemzContract(walletNetwork);
    if (!loadedTraxxStemzContract) return;

    try {
      const userMintCount = await (
        await loadedTraxxStemzContract.tokensOfOwner(walletAddress)
      ).length;
      setPrimaryMintCount(userMintCount);
    } catch (e) {
      setPrimaryMintCount(0);
    }
  }, [walletAddress, walletNetwork, traxxStemzContract, modalVariant]);

  useEffect(() => {
    getUserMintInfo();
  }, [getUserMintInfo]);

  const handleMintWithCrypto = useCallback(async () => {
    try {
      if (!walletNetwork) throw new Error('Network is not defined');
      if (!walletAddress) throw new Error('Wallet account is not defined');

      const loadedTraxxStemzContract = await traxxStemzContract(walletNetwork);
      if (!loadedTraxxStemzContract)
        throw new Error('Could not load traxxstemz contract');

      // TODO: Add mint functionality
      // window.alert('Mint was pressed! Functionality to be implemented!');
      // return null;
      // let {maxFeePerGas, maxPriorityFeePerGas} =
      //   await loadedTraxxStemzContract.provider.getFeeData();
      // const fastestGas = await axios
      //   .get(
      //     `https://api.etherscan.io/api?module=gastracker&action=gasoracle&apikey=49BCUM32ESMTPW2WCMZMGRJGR8JDDB5N62`,
      //   )
      //   .catch((e) => {
      //     return null;
      //   });
      // const fee = fastestGas?.data?.result.suggestBaseFee || null;
      // if (fee) {
      //   maxFeePerGas = parseUnits(Math.ceil(Number(fee)).toString(), 'gwei');
      //   maxPriorityFeePerGas = parseUnits(
      //     Math.ceil(Number(fee)).toString(),
      //     'gwei',
      //   );
      // }

      return loadedTraxxStemzContract.mint(purchaseQuantity, [], {
        value: BigNumber.from(buyoutPrice).mul(purchaseQuantity),
        // maxFeePerGas,
        // maxPriorityFeePerGas,
        // gasLimit: BigNumber.from('0x1E8480'),
      });
    } catch (e) {
      toast.addPaymentErrorToast(
        artist || '',
        trackName || '',
        '#XXXXXX',
        'Your transaction could not be completed',
      );
      return null;
    }
  }, [
    artist,
    toast,
    trackName,
    traxxStemzContract,
    walletNetwork,
    walletAddress,
    purchaseQuantity,
    buyoutPrice,
  ]);

  const {convertCryptoToUSD: convertETHToUSD} = usePriceConversion({
    primaryCurrency: 'ETH',
    shouldPoll: false,
  });

  const handleEstimateGasFee = useCallback(async () => {
    try {
      if (!walletNetwork) throw new Error('Network is not defined');
      if (!walletAddress) throw new Error('Wallet account is not defined');

      const loadedTraxxStemzContract = await traxxStemzContract(walletNetwork);
      if (!loadedTraxxStemzContract)
        throw new Error('Could not load traxxstemz contract');

      // TODO: Add mint functionality
      // window.alert('Mint was pressed! Functionality to be implemented!');
      // return null;
      // let {maxFeePerGas, maxPriorityFeePerGas} =
      //   await loadedTraxxStemzContract.provider.getFeeData();
      // const fastestGas = await axios
      //   .get(
      //     `https://api.etherscan.io/api?module=gastracker&action=gasoracle&apikey=49BCUM32ESMTPW2WCMZMGRJGR8JDDB5N62`,
      //   )
      //   .catch((e) => {
      //     return null;
      //   });
      // const fee = fastestGas?.data?.result.suggestBaseFee || null;
      // if (fee) {
      //   maxFeePerGas = parseUnits(Math.ceil(Number(fee)).toString(), 'gwei');
      //   maxPriorityFeePerGas = parseUnits(
      //     Math.ceil(Number(fee)).toString(),
      //     'gwei',
      //   );
      // }
      const buyNft = () => {
        try {
          return loadedTraxxStemzContract
            .connect(
              new VoidSigner(
                process.env.ADMIN_ADDRESS as string,
                loadedTraxxStemzContract.provider as any,
              ),
            )
            .estimateGas.delegateMint(
              walletAddress,
              BigNumber.from(purchaseQuantity),
              [],
              {value: BigNumber.from(buyoutPrice).mul(purchaseQuantity)},
            );
        } catch (e) {
          return '200000';
        }
      };

      const gasPrice = await loadedTraxxStemzContract.provider.getGasPrice();
      return convertETHToUSD(gasPrice.mul(await buyNft()));
    } catch (e) {
      return '200000';
    }
  }, [
    artist,
    toast,
    trackName,
    traxxStemzContract,
    walletNetwork,
    walletAddress,
    purchaseQuantity,
    buyoutPrice,
  ]);

  const handleMintWithCash = async (values: PaymentDetailsFormProps) => {
    return buyWithCash({
      values,
      ipAddress,
      cashTotal: monetaryValues.cash.total,
      url,
      setModalVariant,
      checkout,
      userId,
      id,
      listingId: 'traxxStemz',
      purchaseQuantity,
      buyoutPrice,
      router,
    });
  };

  const {modalVariantComponent, onPressPurchase} = useCheckoutModals({
    currencySymbol,
    network,
    modalVariant,
    setModalVariant,
    canUseCash,
    props: {
      paymentStatus,
      onStartCashCheckout: handleMintWithCash,
      onStartCryptoCheckout: handleMintWithCrypto as any,
      onEstimateGasFee: handleEstimateGasFee,
      quantity: purchaseQuantity,
      trackName,
      artist,
      imageSrc,
      userEmail,
      ...monetaryValues,
      onIncrement,
      onDecrement,
      onResetQuantity,
      loading: false,
      isTraxxStemz: true,
      primarySaleUserCount: primaryMintCount,
      maxPurchaseLimit: USER_MAX_MINT_LIMIT,
      isAllowedUser,
      buyoutPrice: buyoutPrice.toString(),
      nftId: id || '',
      listingId: 'traxxStemz',
      url,
      slug: 'graffiti6',
      country,
    },
    isMember: false,
  });

  // Used when the user tries to repurchase an NFT using my-transactions
  useEffect(() => {
    if (router.query.checkout) {
      onPressPurchase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.checkout]);

  return {
    handleMintWithCrypto,
    handleMintWithCash,
    handleEstimateGasFee,
    monetaryValues,
    paymentStatus,
    modalVariant,
    modalVariantComponent,
    setModalVariant,
    handleStartCheckoutFlow: onPressPurchase,
    loadingTreasuryBalance,
  };
};
